import { useCallback } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { Button, useFormField, DropdownField, SearchField, styles } from '@frontend/design-system';
import { labelMap } from '../constants';
import { useLocationPicker } from '../store';
import { LocationPickerTestingIds } from '../testing-ids';
import { LocationPickerTrackingIds } from '../tracking-ids';
import { LocationSearchFilter, LocationSearchFilterProps } from './location-search-filter';

type LocationPickerHeaderProps = {
  children: React.ReactNode;
  count: LocationSearchFilterProps['count'];
};

export const LocationPickerHeaderSearch = ({ children, count }: LocationPickerHeaderProps) => {
  const {
    isSearchMode,
    setIsSearchMode,
    searchTerm,
    setSearchTerm,
    activeSearchFilter,
    resetSearch,
    hasSingleLocationGroup,
    hasSingleOrganization,
    isPrivilegedPicker,
  } = useLocationPicker([
    'isSearchMode',
    'setIsSearchMode',
    'searchTerm',
    'setSearchTerm',
    'activeSearchFilter',
    'resetSearch',
    'hasSingleLocationGroup',
    'hasSingleOrganization',
    'isPrivilegedPicker',
  ]);

  const searchFieldProps = useFormField(
    {
      type: 'text',
      value: searchTerm,
    },
    [searchTerm]
  );

  const handleEnterSearchMode = useCallback(() => {
    setIsSearchMode(true);
  }, [setIsSearchMode]);

  const handleExitSearchMode = useCallback(() => {
    resetSearch();
    setIsSearchMode(false);
  }, [setIsSearchMode, resetSearch]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value ?? '';
      setSearchTerm(value);
      searchFieldProps.onChange(e);

      if (!isSearchMode && (hasSingleOrganization || isPrivilegedPicker) && value) {
        setIsSearchMode(true);
      }
    },
    [isSearchMode, hasSingleOrganization, searchFieldProps, setIsSearchMode, setSearchTerm]
  );

  return (
    <>
      <div
        css={[
          css`
            padding: ${theme.spacing(0.25, 2.25)};
            margin-bottom: ${!isSearchMode ? theme.spacing(2) : 0};
          `,
          isSearchMode &&
            hasSingleLocationGroup &&
            hasSingleOrganization && {
              paddingBottom: theme.spacing(2),
              borderBottom: `1px solid ${theme.colors.neutral10}`,
            },
        ]}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: ${theme.spacing(1)};
          `}
        >
          {isSearchMode || hasSingleOrganization || isPrivilegedPicker ? (
            <>
              <AnimatePresence>
                {isSearchMode && (
                  <motion.div initial={{ marginLeft: -40, opacity: 0 }} animate={{ marginLeft: 0, opacity: 1 }}>
                    <Button
                      size='large'
                      css={css`
                        border: 1px solid ${theme.colors.neutral30};
                        width: 40px;
                        span {
                          justify-content: center;
                        }
                        svg {
                          width: 16px;
                        }
                      `}
                      onClick={handleExitSearchMode}
                      variant='secondary'
                      iconName='back-small'
                      trackingId={LocationPickerTrackingIds.backToMainView}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <SearchField
                containerCss={css`
                  flex: 1;
                `}
                label=''
                placeholder={`Search ${labelMap[activeSearchFilter]}`}
                clearable
                autoFocus
                onClear={() => resetSearch()}
                name='search-locations'
                {...searchFieldProps}
                // @ts-ignore
                // event is not typed correctly in design-system
                onChange={handleSearchChange}
              />
            </>
          ) : (
            <>
              {children}
              <Button
                size='large'
                css={css`
                  border: 1px solid ${theme.colors.neutral30};
                `}
                onClick={handleEnterSearchMode}
                variant='secondary'
                iconName='search'
                trackingId={LocationPickerTrackingIds.search}
                data-testid={LocationPickerTestingIds.searchButton}
              />
            </>
          )}
        </div>
      </div>
      {isSearchMode && <LocationSearchFilter count={count} />}
    </>
  );
};

interface LocationPickerHeaderOrganizationsProps {
  organizationIds: string[];
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
}

const LocationPickerHeaderOrganizations = ({
  organizationIds = [],
  getScopeName,
  renderOrgInfo,
}: LocationPickerHeaderOrganizationsProps) => {
  if (organizationIds.length === 1) {
    return null;
  }
  return (
    <LocationPickerHeaderOrganizationsDropdown
      renderOrgInfo={renderOrgInfo}
      organizationIds={organizationIds}
      getScopeName={getScopeName}
    />
  );
};

export const LocationPickerHeaderOrganizationsDropdown = ({
  organizationIds,
  getScopeName,
  renderOrgInfo,
}: LocationPickerHeaderOrganizationsProps) => {
  const { organizationViewId, setOrganizationViewId } = useLocationPicker([
    'organizationViewId',
    'setOrganizationViewId',
  ]);
  const organizationFieldProps = useFormField(
    {
      type: 'dropdown',
      value: organizationViewId,
    },
    [organizationViewId]
  );

  return (
    <div
      css={css`
        width: 100%;
        flex: 1;
        min-width: 0;
        > div {
          width: 100%;
          flex: 1;
        }
      `}
    >
      <DropdownField
        {...organizationFieldProps}
        onChange={(e) => {
          organizationFieldProps.onChange(e);
          //@ts-ignore
          setOrganizationViewId(e.value);
        }}
        name='organizations'
        label='Organizations'
      >
        {organizationIds.map((organizationId) => (
          <DropdownField.Option key={organizationId} value={organizationId}>
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
              `}
            >
              <span css={[styles.truncate]}>{getScopeName(organizationId, 'org')}</span>
              {renderOrgInfo?.(organizationId)}
            </span>
          </DropdownField.Option>
        ))}
      </DropdownField>
    </div>
  );
};

interface LocationPickerHeader extends LocationPickerHeaderOrganizationsProps {
  count: LocationSearchFilterProps['count'];
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
}

export const LocationPickerHeader = ({ organizationIds, getScopeName, count, renderOrgInfo }: LocationPickerHeader) => (
  <LocationPickerHeaderSearch count={count}>
    <LocationPickerHeaderOrganizations
      organizationIds={organizationIds}
      getScopeName={getScopeName}
      renderOrgInfo={renderOrgInfo}
    />
  </LocationPickerHeaderSearch>
);
